import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import TrackGroupedSchedule from "../components/TrackGroupedSchedule";
import { COUNTRIES, JOB_ROLES } from "../lib/countries";
import {
  Stethoscope, Calendar, Globe2, PlayCircle, Sparkles,
  CheckCircle2, ArrowRight, Clock, Users, ChevronRight, Heart, Lock,
} from "lucide-react";

const SPEAKERS = [
  { id: "s1", name: "Damian Roland", role: "Professor, Paediatric Emergency Medicine", topic: "Improving Care of the Ill & Injured Child",
    picture: "https://dftb26.com/wp-content/uploads/2026/04/Damian_Roland.jpg" },
  { id: "s2", name: "Sarah McNab", role: "Director of General Medicine, RCH Melbourne", topic: "General Paediatrics & Inpatient Research",
    picture: "https://dftb26.com/wp-content/uploads/2025/10/Sarah-McNab.jpg" },
  { id: "s3", name: "Dennis Ren", role: "Paediatric EM Attending, Children's National", topic: "Critical Appraisal in Paediatric EM",
    picture: "https://dftb26.com/wp-content/uploads/2026/03/Dennis-Ren.jpg" },
  { id: "s4", name: "Dani Hall", role: "PEM Consultant & Director of Simulation, CHI", topic: "Simulation & Inclusive Care",
    picture: "https://dftb26.com/wp-content/uploads/2026/03/Dani-Hall.jpg" },
  { id: "s5", name: "Simon Carley", role: "Dean of RCEM · Professor, EM", topic: "Evidence-Based Emergency Medicine",
    picture: "https://dftb26.com/wp-content/uploads/2026/03/Simon-Carley.jpg" },
  { id: "s6", name: "Becky Platt", role: "Advanced Clinical Practitioner in Paediatric Emergency Medicine", topic: "The Sentence You Live By",
    picture: "https://dftb26.com/wp-content/uploads/2026/03/Becky-Platt-Img.jpg" },
  { id: "s7", name: "Shrouk Messahel", role: "Consultant PEM, Alder Hey Children's Hospital", topic: "ED Research & Trauma",
    picture: "https://dftb26.com/wp-content/uploads/2026/03/Shrouk-Massahel.jpg" },
  { id: "s8", name: "Dal Hothi", role: "Consultant Paediatric Nephrologist, GOSH", topic: "Leadership & QI",
    picture: "https://dftb26.com/wp-content/uploads/2026/03/Daljit-Hothi.jpg" },
  { id: "s9", name: "Elaine Cole", role: "Professor of Trauma Sciences, QMUL", topic: "Severe Injury & Critical Care",
    picture: "https://dftb26.com/wp-content/uploads/2026/03/Elaine-Cole.jpg" },
  { id: "s10", name: "Ken Milne", role: "Creator, The Skeptics' Guide to EM", topic: "Skepticism & Critical Thinking",
    picture: "https://dftb26.com/wp-content/uploads/2026/03/Ken-Milne.jpg" },
  { id: "s11", name: "Ed Pooley", role: "UK GP, Communication Skills Trainer", topic: "Consultation Skills",
    picture: "https://dftb26.com/wp-content/uploads/2026/03/Ed-Pooley.jpg" },
  { id: "s12", name: "Chella Quint OBE", role: "Educator, Author & Period Positive Founder", topic: "Adolescent Health",
    picture: "https://dftb26.com/wp-content/uploads/2026/03/Chella-Quint.jpg" },
];

const FAQ = [
  { q: "Is this a virtual or in-person ticket?", a: "DFTB26Virtual.com sells the virtual streaming ticket only - you'll get a login to watch every session live and on-demand from anywhere in the world. If you'd like to attend in person at the SEC Glasgow, in-person tickets are sold separately on DFTB26.com." },
  { q: "When and where is DFTB26?", a: "DFTB26 'Back To Basics' streams 22-24 June 2026 live from the SEC Glasgow. You can attend the virtual conference from anywhere in the world." },
  { q: "What's included with my Virtual ticket?", a: "On-demand access to every keynote, concurrent session and panel from DFTB26 — full-day recordings now, individual talks added as they're cut. Watch any session as many times as you like until 22 June 2027." },
  { q: "What if I can't attend live?", a: "Every session is recorded and made available in the portal during the 12-month replay window. There is no permanent post-conference on-demand library." },
  { q: "Is there a concession rate for nurses, resident doctors or trainees?", a: "Yes - the concession tier is for nurses, resident (junior) doctors and trainees. You'll be asked to confirm your role at checkout. Early-bird £247 (until 14 June 2026), then £277." },
  { q: "I work in a low- or middle-income country (LMIC) - can I get free access?", a: "Yes. Apply via the 'LMIC free access' form below and we'll review and approve eligible applications manually. Please include details of your hospital, role and the realities of your local resources." },
  { q: "When do ticket sales close?", a: "Sales close 24 hours after the conference ends (around 25 June 2026). After that point new tickets cannot be purchased - existing ticket holders keep their 12-month replay access." },
];

function formatGBP(n) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(n);
}

function LMICApplyDialog() {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", country: "",
    job_role: "", job_role_other: "", hospital: "", reason: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/lmic/apply", form);
      toast.success("Application received. We review every application manually - you'll hear from us by email.");
      setOpen(false);
      setForm({
        full_name: "", email: "", country: "",
        job_role: "", job_role_other: "", hospital: "", reason: "",
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="mt-5 rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white" data-testid="lmic-apply-open-btn">
          Apply for free access
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="lmic-apply-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">LMIC free-access application</DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            Applications are reviewed manually. Please tell us about your work so we can prioritise places.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="lmic-apply-form">
          <div>
            <Label htmlFor="lmic-name" className="text-xs">Full name</Label>
            <Input id="lmic-name" required minLength={2} value={form.full_name}
                   onChange={(e) => update("full_name", e.target.value)} data-testid="lmic-name-input" />
          </div>
          <div>
            <Label htmlFor="lmic-email" className="text-xs">Work email</Label>
            <Input id="lmic-email" type="email" required value={form.email}
                   onChange={(e) => update("email", e.target.value)} data-testid="lmic-email-input" />
          </div>
          <div>
            <Label htmlFor="lmic-country" className="text-xs">Country you work in</Label>
            <select
              id="lmic-country"
              required
              value={form.country}
              onChange={(e) => update("country", e.target.value)}
              data-testid="lmic-country-input"
              className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#28BBE3]/40"
            >
              <option value="">Select country…</option>
              {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor="lmic-role" className="text-xs">Job role</Label>
            <select
              id="lmic-role"
              required
              value={form.job_role}
              onChange={(e) => update("job_role", e.target.value)}
              data-testid="lmic-role-input"
              className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#28BBE3]/40"
            >
              <option value="">Select role…</option>
              {JOB_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          {form.job_role === "Other" && (
            <div>
              <Label htmlFor="lmic-role-other" className="text-xs">Please specify your role</Label>
              <Input id="lmic-role-other" required minLength={2} value={form.job_role_other}
                     onChange={(e) => update("job_role_other", e.target.value)}
                     data-testid="lmic-role-other-input" />
            </div>
          )}
          <div>
            <Label htmlFor="lmic-hospital" className="text-xs">Hospital / institution</Label>
            <Input id="lmic-hospital" required minLength={2}
                   placeholder="e.g. Kenyatta National Hospital"
                   value={form.hospital}
                   onChange={(e) => update("hospital", e.target.value)} data-testid="lmic-hospital-input" />
          </div>
          <div>
            <Label htmlFor="lmic-reason" className="text-xs">Why is free access important for you? (min 20 chars)</Label>
            <Textarea id="lmic-reason" required minLength={20} rows={4}
                      placeholder="A few lines about your local context - access to journals, training opportunities, etc."
                      value={form.reason}
                      onChange={(e) => update("reason", e.target.value)} data-testid="lmic-reason-input" />
          </div>
          <Button type="submit" disabled={submitting}
                  className="w-full rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white py-5"
                  data-testid="lmic-submit-btn">
            {submitting ? "Submitting…" : "Submit application"}
          </Button>
          <p className="text-xs text-slate-500 text-center">We aim to review applications within 5 working days.</p>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    api.get("/packages").then(r => setPricing(r.data)).catch(() => {});
  }, []);

  const handleCheckout = (package_id) => {
    if (pricing && !pricing.sales_open) {
      toast.error("Ticket sales have closed.");
      return;
    }
    navigate(`/checkout?package=${package_id}`);
  };

  return (
    <div className="App relative">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-20 pb-20 sm:pt-24 sm:pb-28 overflow-hidden">
        {/* Decorative background blobs */}
        <div className="absolute top-24 left-12 h-24 w-24 rounded-full bg-[#28BBE3]/20 blur-2xl dftb-float-slow -z-10" />
        <div className="absolute top-40 right-24 h-40 w-40 rounded-full bg-[#4A90E2]/15 blur-3xl dftb-float-med -z-10" />

        {/* Image card — top-right on lg+, hidden on mobile */}
        <div className="hidden lg:block absolute top-24 right-12 w-[32%] max-w-[440px] rounded-3xl overflow-hidden ring-1 ring-black/5 shadow-2xl shadow-slate-900/10 -z-0 dftb-fade-up">
          <img src="https://customer-assets.emergentagent.com/job_dftb26-virtual/artifacts/iqkkt1i6_DFTB30.png"
               alt="DFTB delegates connecting between sessions" className="w-full h-auto" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/60 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-80">From the in-person stage</div>
            <div className="font-heading text-base font-medium mt-1">Streamed to your screen</div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 sm:px-10">
          <div className="dftb-fade-up">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/90 ring-1 ring-black/5 text-xs uppercase tracking-[0.22em] text-[#DC4F48] font-bold backdrop-blur-md">
              <span className="h-1.5 w-1.5 rounded-full bg-[#DC4F48]" />
              Now on demand · 60+ talks · Watch until 22 June 2027
            </div>

            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-black text-slate-900 leading-[1.05] lg:max-w-[62%] xl:max-w-[58%]">
              Back To <span className="text-[#DC4F48]">Basics.</span> The acute paediatric conference, <span className="italic font-medium text-[#28BBE3]">on demand.</span>
            </h1>

            <p className="mt-5 max-w-2xl text-base sm:text-lg text-slate-700 leading-relaxed">
              DFTB26 has wrapped — and every keynote, concurrent and panel from the SEC Glasgow stage
              is now available to stream on demand. Full-day recordings are live now, with individual
              talks added as they&apos;re cut. <strong className="text-slate-900">12 months</strong> of replay
              access included.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-4">
              <Button onClick={() => { document.getElementById("tickets")?.scrollIntoView({behavior:"smooth"}); }}
                className="rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white px-7 py-5 text-base font-bold shadow-lg shadow-[#28BBE3]/25"
                data-testid="hero-cta-checkout-btn">
                Get on-demand access
                <ArrowRight className="ml-2" size={18} strokeWidth={2.5} />
              </Button>
              <a href="#schedule" className="inline-flex items-center gap-2 text-slate-700 hover:text-slate-900 px-4 py-3" data-testid="hero-schedule-link">
                <PlayCircle size={20} strokeWidth={1.5} />
                <span className="text-sm font-medium">Browse the talks</span>
              </a>
            </div>

            {/* Pricing strip + clarifier */}
            <div className="mt-7 max-w-2xl" data-testid="hero-pricing-strip">
              {pricing && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {["virtual_full", "virtual_concession"].map((pid) => {
                    const pkg = pricing.packages?.[pid];
                    if (!pkg) return null;
                    const isConc = pid === "virtual_concession";
                    return (
                      <a key={pid} href="#tickets"
                         className="group flex items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-white/90 ring-1 ring-black/5 hover:ring-[#28BBE3]/40 hover:bg-white backdrop-blur-md transition-all"
                         data-testid={`hero-price-${pid}`}>
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">
                            {isConc ? "Concession" : "Full access"}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {isConc ? "Nurses & trainees" : "Standard ticket"}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-baseline gap-1.5">
                            <span className="font-heading text-2xl font-black text-slate-900">{formatGBP(pkg.current_amount)}</span>
                            {pricing.is_early_bird && (
                              <span className="text-xs text-slate-400 line-through">{formatGBP(pkg.standard_amount)}</span>
                            )}
                          </div>
                          {pricing.is_early_bird && (
                            <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#DC4F48] mt-0.5">
                              Early-bird
                            </div>
                          )}
                        </div>
                      </a>
                    );
                  })}
                </div>
              )}
              <div className="mt-3 flex items-center gap-2 text-xs text-slate-600">
                <Globe2 size={14} className="text-[#28BBE3] flex-shrink-0" strokeWidth={1.8} />
                <span>
                  Free LMIC scholarships available ·{" "}
                  <a href="#tickets" className="font-semibold text-[#DC4F48] hover:underline" data-testid="hero-tickets-link">
                    full details →
                  </a>
                </span>
              </div>
            </div>

            <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-10 max-w-3xl">
              {[
                { k: "3 days", v: "of live programming" },
                { k: "40+", v: "speakers & faculty" },
                { k: "12 months", v: "replay access" },
                { k: "Worldwide", v: "stream from anywhere" },
              ].map((it) => (
                <div key={it.k}>
                  <div className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900">{it.k}</div>
                  <div className="text-xs sm:text-sm text-slate-600 mt-1">{it.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-black/5 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-6 flex flex-wrap items-center justify-between gap-6 text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">
          <div className="flex items-center gap-2"><Stethoscope size={16} strokeWidth={1.5} /> Acute Paediatrics</div>
          <div>Don't Forget The Bubbles</div>
          <div>est. 2013</div>
          <div className="hidden sm:flex items-center gap-2"><Globe2 size={16} strokeWidth={1.5} /> 60+ countries</div>
          <div className="hidden md:flex items-center gap-2"><Sparkles size={16} strokeWidth={1.5} /> 12-month replay</div>
        </div>
      </section>

      {/* WHY VIRTUAL - BENTO */}
      <section className="py-24 sm:py-32 px-6 sm:px-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Why DFTB26 Virtual</div>
          <h2 className="mt-3 font-heading text-3xl sm:text-5xl tracking-tighter font-medium text-slate-900 max-w-3xl">
            World-class paediatric education, <span className="italic font-normal text-[#28BBE3]">without the airport.</span>
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-6 gap-6">
            <div className="md:col-span-4 rounded-3xl bg-white ring-1 ring-black/5 p-10 relative overflow-hidden">
              <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-[#28BBE3]/10 blur-2xl" />
              <Calendar className="text-[#28BBE3]" size={28} strokeWidth={1.5} />
              <h3 className="mt-6 font-heading text-2xl sm:text-3xl text-slate-900 font-medium tracking-tight">
                Three days of live streaming.
              </h3>
              <p className="mt-3 text-slate-600 max-w-xl leading-relaxed">
                Tune in live across all three conference days. Stream from your couch, your call room,
                or between cases - full HD video and crisp audio, optimized for low bandwidth too.
              </p>
            </div>

            <div className="md:col-span-2 rounded-3xl bg-[#0F172A] text-white p-8 relative overflow-hidden">
              <div className="absolute -right-6 -bottom-6 h-32 w-32 rounded-full bg-[#28BBE3]/40 blur-2xl" />
              <Users size={24} strokeWidth={1.5} />
              <h3 className="mt-5 font-heading text-xl font-medium">40+ speakers</h3>
              <p className="mt-2 text-sm text-white/70 leading-relaxed">
                The educators behind Don't Forget The Bubbles plus invited international faculty.
              </p>
            </div>

            <div className="md:col-span-2 rounded-3xl bg-[#F3F2F0] ring-1 ring-black/5 p-8">
              <Clock className="text-slate-900" size={24} strokeWidth={1.5} />
              <h3 className="mt-5 font-heading text-xl text-slate-900 font-medium">12-month replay</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                Every session stays in the portal for 12 months after the conference - rewatch anytime until 22 June 2027.
              </p>
            </div>

            <div className="md:col-span-4 rounded-3xl bg-white ring-1 ring-black/5 p-10 relative overflow-hidden">
              <img src="https://customer-assets.emergentagent.com/job_dftb26-virtual/artifacts/9e380dg4_DFTB19.png"
                   alt="DFTB delegates relaxing between sessions" className="absolute right-0 inset-y-0 w-1/2 h-full object-cover opacity-90" />
              <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-transparent" />
              <div className="relative max-w-md">
                <Sparkles className="text-[#4A90E2]" size={28} strokeWidth={1.5} />
                <h3 className="mt-6 font-heading text-2xl sm:text-3xl text-slate-900 font-medium tracking-tight">
                  Watch live. Rewatch all year.
                </h3>
                <p className="mt-3 text-slate-600 leading-relaxed">
                  Tune in across the three conference days, then revisit any session inside the portal for a full 12 months. Sales close 24 hours after the conference, so grab your ticket while the early-bird price is live.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SPEAKERS */}
      <section id="speakers" className="py-24 sm:py-32 px-6 sm:px-10 bg-[#F3F2F0] relative dftb-grain overflow-hidden">
        <div className="max-w-6xl mx-auto relative">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">The faculty</div>
              <h2 className="mt-3 font-heading text-3xl sm:text-5xl tracking-tight font-black text-slate-900">
                Voices that <span className="italic font-medium text-[#DC4F48]">shape</span> acute paediatrics.
              </h2>
            </div>
            <p className="text-slate-600 max-w-md leading-relaxed">
              Featured speakers from the DFTB26 faculty. See the full lineup of 40+ speakers on{" "}
              <a href="https://dftb26.com/speakers/" target="_blank" rel="noreferrer" className="text-[#28BBE3] hover:underline">DFTB26.com</a>.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SPEAKERS.map((s, i) => (
              <div key={s.id} className="group rounded-3xl bg-white ring-1 ring-black/5 overflow-hidden hover:-translate-y-1 transition-transform duration-300"
                   data-testid={`speaker-card-${s.id}`}>
                <div className="aspect-[5/4] overflow-hidden">
                  <img src={s.picture} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Speaker · {String(i + 1).padStart(2, "0")}</div>
                  <h3 className="mt-2 font-heading text-xl text-slate-900 font-medium tracking-tight">{s.name}</h3>
                  <p className="text-sm text-slate-600 mt-1">{s.role}</p>
                  <div className="mt-4 pt-4 border-t border-black/5 flex items-center justify-between text-sm">
                    <span className="text-slate-700">{s.topic}</span>
                    <ChevronRight size={16} className="text-slate-400 group-hover:text-[#28BBE3] transition-colors" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SCHEDULE */}
      <section id="schedule" className="py-24 sm:py-32 px-6 sm:px-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">The schedule</div>
          <h2 className="mt-3 font-heading text-3xl sm:text-5xl tracking-tight font-black text-slate-900 max-w-2xl">
            Three days. <span className="italic font-medium text-[#DC4F48]">Every speaker, every talk.</span>
          </h2>
          <p className="mt-4 text-sm text-slate-500 max-w-2xl">
            Programme syncs nightly with{" "}
            <a href="https://dftb26.com/day-1/" target="_blank" rel="noreferrer" className="text-[#28BBE3] hover:underline">DFTB26.com</a>.
            Each concurrent slot runs across our three streaming rooms - jump between Plenary, Stream 2 and Stream 3 inside the portal.
          </p>

          <div className="mt-14">
            <TrackGroupedSchedule variant="dark" />
          </div>
        </div>
      </section>

      {/* TICKETS / PRICING */}
      <section id="tickets" className="py-24 sm:py-32 px-6 sm:px-10 bg-[#0F172A] text-white relative overflow-hidden">
        <div className="absolute -left-20 top-20 h-80 w-80 rounded-full bg-[#28BBE3]/25 blur-3xl dftb-float-slow" />
        <div className="absolute right-0 bottom-10 h-72 w-72 rounded-full bg-[#4A90E2]/20 blur-3xl dftb-float-med" />
        <div className="max-w-6xl mx-auto relative">

          <div className="text-xs uppercase tracking-[0.22em] text-white/60 font-semibold">Your ticket</div>
          <h2 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-black leading-tight max-w-3xl">
            Pick your pass. <span className="italic font-medium text-[#DC4F48]">Stream the world.</span>
          </h2>
          <p className="mt-5 text-white/70 leading-relaxed max-w-2xl">
            Every ticket includes <strong className="text-white">12 months</strong> of on-demand access to every keynote, concurrent and panel from DFTB26 — until 22 June 2027.
          </p>

          {pricing && !pricing.sales_open && (
            <div className="mt-8 rounded-2xl bg-rose-500/15 ring-1 ring-rose-300/40 px-5 py-4 text-rose-100 max-w-2xl flex items-center gap-3"
                 data-testid="sales-closed-banner">
              <Lock size={18} />
              <span><strong>Ticket sales are closed.</strong> The window for new purchases ended 24 hours after the conference. Existing ticket holders — your portal access continues until 22 June 2027.</span>
            </div>
          )}

          {pricing && (
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
              {["virtual_full", "virtual_concession"].map((pid) => {
                const pkg = pricing.packages[pid];
                if (!pkg) return null;
                const isConcession = pid === "virtual_concession";
                return (
                  <div key={pid}
                       className={`relative rounded-3xl bg-white text-slate-900 p-8 sm:p-10 ring-1 ring-white/20 shadow-2xl shadow-black/30 flex flex-col ${isConcession ? "" : "md:scale-[1.02]"}`}
                       data-testid={`pricing-card-${pid}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold">
                        {isConcession ? "Concession" : "Standard"}
                      </div>
                      {pricing.is_early_bird && (
                        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#DC4F48] bg-[#DC4F48]/10 px-2.5 py-1 rounded-full">
                          Early-bird
                        </span>
                      )}
                    </div>

                    <h3 className="mt-3 font-heading text-2xl tracking-tight text-slate-900 font-medium">
                      {isConcession ? "Nurses & Trainees" : "Full Access"}
                    </h3>
                    <p className="mt-1 text-xs text-slate-500">
                      {isConcession ? "Nurses, resident (junior) doctors and trainees." : "For consultants, specialists and everyone else."}
                    </p>

                    <div className="mt-6 flex items-baseline gap-3">
                      <span className="font-heading text-5xl sm:text-6xl font-black tracking-tight">
                        {formatGBP(pkg.current_amount)}
                      </span>
                      {pricing.is_early_bird && (
                        <span className="text-sm text-slate-400 line-through">
                          {formatGBP(pkg.standard_amount)}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      {pricing.is_early_bird
                        ? `Early-bird until end of 14 June 2026 · then ${formatGBP(pkg.standard_amount)}`
                        : `Standard price · sales close 25 June 2026`}
                    </div>

                    <ul className="mt-7 space-y-2.5 text-sm text-slate-700">
                      {[
                        "3 days of live streaming (22-24 June 2026)",
                        "Every keynote, panel, and workshop included",
                        "12-month replay access in the portal",
                        "Stream from anywhere in the world",
                        "Email login sent to you after checkout",
                      ].map((b) => (
                        <li key={b} className="flex items-start gap-2.5">
                          <CheckCircle2 size={16} className="text-[#28BBE3] flex-shrink-0 mt-0.5" strokeWidth={2} />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>

                    {isConcession && (
                      <div className="mt-6 rounded-xl bg-[#F3F2F0] px-4 py-3 text-xs text-slate-600 leading-relaxed"
                           data-testid="concession-note">
                        For <strong>nurses, resident doctors and trainees</strong>. You'll confirm your role at checkout.
                      </div>
                    )}

                    <Button
                      onClick={() => handleCheckout(pid)}
                      disabled={pricing && !pricing.sales_open}
                      className={`mt-auto w-full rounded-full py-6 text-base font-semibold shadow-lg ${
                        isConcession
                          ? "bg-slate-900 hover:bg-slate-800 text-white shadow-slate-900/30"
                          : "bg-[#28BBE3] hover:bg-[#1FA8CC] text-white shadow-[#28BBE3]/30"
                      }`}
                      data-testid={`pricing-cta-${pid}-btn`}
                    >
                      <span>Get this ticket</span>
                      <span className="ml-2 opacity-80">{formatGBP(pkg.current_amount)}</span>
                      <ArrowRight className="ml-2" size={17} strokeWidth={2} />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          {/* LMIC + in-person clarifier */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-3xl bg-white/5 ring-1 ring-white/10 backdrop-blur-md p-8 text-white"
                 data-testid="lmic-card">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-[#28BBE3]/20 flex items-center justify-center">
                  <Heart size={18} className="text-[#28BBE3]" strokeWidth={2} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-white/60 font-semibold">Free Access</div>
                  <h3 className="font-heading text-xl tracking-tight">LMIC scholarship</h3>
                </div>
              </div>
              <p className="mt-4 text-sm text-white/75 leading-relaxed">
                If you work in a low- or middle-income country, apply for free access. Each application is reviewed manually to make sure scholarships reach the colleagues who need them most.
              </p>
              <LMICApplyDialog />
            </div>

            <div className="rounded-3xl bg-white/5 ring-1 ring-white/10 backdrop-blur-md p-8 text-white">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-[#DC4F48]/20 flex items-center justify-center">
                  <Globe2 size={18} className="text-[#DC4F48]" strokeWidth={2} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-white/60 font-semibold">In-person?</div>
                  <h3 className="font-heading text-xl tracking-tight">Buy in-person tickets</h3>
                </div>
              </div>
              <p className="mt-4 text-sm text-white/75 leading-relaxed">
                This site sells <strong className="text-white">virtual streaming tickets only</strong>. For tickets to attend in person at the SEC Glasgow, head to the main conference site.
              </p>
              <a href="https://dftb26.com" target="_blank" rel="noreferrer"
                 className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-[#28BBE3] transition-colors"
                 data-testid="inperson-link">
                DFTB26.com →
              </a>
            </div>
          </div>

          {user ? (
            <p className="mt-8 text-xs text-white/55 text-center">
              Signed in as {user.email} - your ticket will be linked to this account.
            </p>
          ) : (
            <p className="mt-8 text-xs text-white/55 text-center">
              Already have a ticket?{" "}
              <button onClick={() => navigate("/login")} className="underline text-white/80 hover:text-[#28BBE3]" data-testid="pricing-login-link">
                Log in
              </button>
            </p>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 sm:py-32 px-6 sm:px-10">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Frequently asked</div>
          <h2 className="mt-3 font-heading text-3xl sm:text-5xl tracking-tight font-black text-slate-900">
            Questions, <span className="italic font-medium text-[#DC4F48]">answered.</span>
          </h2>

          <div className="mt-12 space-y-4">
            {FAQ.map((f, i) => (
              <details key={i} className="group rounded-2xl bg-white ring-1 ring-black/5 px-6 py-5 open:shadow-lg open:shadow-black/5 transition-all" data-testid={`faq-item-${i}`}>
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
                  <span className="font-heading text-lg text-slate-900 font-medium">{f.q}</span>
                  <span className="h-7 w-7 rounded-full bg-[#F3F2F0] flex items-center justify-center group-open:bg-[#28BBE3] group-open:text-white transition-colors text-slate-700 text-lg leading-none">+</span>
                </summary>
                <p className="mt-4 text-slate-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-black/5 bg-white py-12 px-6 sm:px-10">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-6">
          <div className="flex items-baseline gap-0.5">
            <span className="font-heading text-xl font-black text-[#212121]">DFTB</span>
            <span className="font-heading text-xl font-black text-[#28BBE3]">26</span>
            <span className="ml-2 text-xs uppercase tracking-[0.22em] text-slate-500 font-bold">Virtual</span>
          </div>
          <div className="text-xs text-slate-500">© 2026 Don't Forget The Bubbles · DFTB26 'Back To Basics' · 22-24 June 2026 · SEC Glasgow</div>
          <div className="flex items-center gap-5 text-xs text-slate-600">
            <a href="mailto:registration@dontforgetthebubbles.com" className="hover:text-[#28BBE3]" data-testid="footer-support">Support</a>
            <a href="https://dftb26.com" className="hover:text-[#28BBE3]" target="_blank" rel="noreferrer" data-testid="footer-dftb26">DFTB26.com</a>
            <a href="#tickets" className="hover:text-[#28BBE3]">Tickets</a>
            <a href="#faq" className="hover:text-[#28BBE3]">FAQ</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
